//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import ProviderVisitsModel from './providerVisits.model';

/**
 * @service ProviderVisitsService
 * @description Service for managing provider visits within the Trading Community module.
 * This service handles the retrieval of visit logs, detailed visit information, and the generation of visit reports.
 * Provider visits track the interactions between service providers (technicians/managers) and customers (farmers/dealers).
 *
 * It interacts with the `/blubay/providervisits/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Visit Retrieval**: Fetching lists of visits associated with a specific provider business.
 * 2. **Detail View**: Retrieving comprehensive details for a single visit, including related entities (products placed, observations, etc.).
 * 3. **Reporting**: Generating reports based on dynamic criteria.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve visits by Provider Business ID.
 * @feature 2. Get detailed visit information.
 * @feature 3. Generate visit reports.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<ProviderVisitsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/providervisits/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})

export class ProviderVisitsService extends Service<ProviderVisitsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/providervisits/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/providervisits/');
  }

  /**
   * @method getVisitsByProviderBusinessId
   * @async
   * @description Fetch all visits associated with a specific provider business account.
   *
   * Retrieves a large dataset (pageSize=200000) of visits, ordered by creation date ('new').
   * Note: The parameter name `business_account_id` suggests filtering by business, but the implementation
   * currently fetches all records without a specific filter query param in the URL string shown,
   * relying potentially on backend context or default behavior.
   *
   * @param {string} business_account_id - The unique identifier of the provider business (currently unused in URL construction).
   * @returns {Promise<Array<ProviderVisitsModel>>} Promise resolving to an array of ProviderVisitsModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVisitsByProviderBusinessId(business_account_id: string): Promise<Array<ProviderVisitsModel>> {
    return new Promise<Array<ProviderVisitsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ProviderVisitsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=200000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getVisitDetailsByVisitId
   * @async
   * @description Fetch detailed information for a specific visit ID.
   *
   * Includes all related data (`include=all`) such as observations, product placements, etc.
   *
   * @param {string} visitId - The unique identifier of the visit.
   * @returns {Promise<any>} Promise resolving to the detailed visit object.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVisitDetailsByVisitId(visitId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.get<any>(`${this.baseUrl}${this.serviceUrl}?id=${visitId}&include=all&page=0&pageSize=200000`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getVisitReports
   * @async
   * @description Generate or retrieve visit reports based on input criteria.
   *
   * Sends a POST request to `visitReport` with a filter object.
   *
   * @param {any} inputObj - Object containing filter criteria for the report (e.g., date range, technician IDs).
   * @returns {Promise<any>} Promise resolving to the report data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVisitReports(inputObj: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.post<any>(`${this.baseUrl}${this.serviceUrl}visitReport`, inputObj, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
